<template>
	<div class="voices-container">
		<Header :title="'Voices'" :user="currentUser" />

		<main v-if="(currentUser as any)?.isAdmin" class="page-main">
			<div class="panel">
				<h2>Things to talk about today</h2>
				<p class="hint">This text can be mentioned by voices and will persist until you change it.</p>
				<textarea v-model="thingsToTalkAboutToday" class="talk-box" rows="4" />
			</div>

			<div class="panel">
				<div class="panel-header">
					<h2>Voice Personas</h2>
					<div class="btn-row">
						<button class="action-btn" @click="addVoice">Add Voice</button>
						<button class="action-btn construct-btn" :disabled="saving" @click="save">Save</button>
					</div>
				</div>

				<div v-if="loading" class="loading">Loading voices...</div>
				<div v-else>
					<div v-for="(v, idx) in voices" :key="idx" class="voice-row">
						<div class="field">
							<label>Voice Name</label>
							<input v-model="v.name" class="input" type="text" />
						</div>
						<div class="field">
							<label>Voice ID</label>
							<input v-model="v.voice_id" class="input" type="text" />
						</div>
						<div class="field" style="grid-column: 1 / -1;">
							<label>Description (rewrite instructions)</label>
							<textarea v-model="v.description" class="input" rows="3" />
						</div>
					</div>
				</div>
			</div>
		</main>

		<main v-else-if="currentUser && !(currentUser as any)?.isAdmin" class="page-main">
			<div class="unauthorized">
				<h2>Access Denied</h2>
				<p>You need admin privileges to view this page.</p>
				<button @click="goDashboard" class="action-btn">Go to Dashboard</button>
			</div>
		</main>

		<main v-else class="page-main">
			<div class="loading">Loading...</div>
		</main>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Header from '../components/Header.vue'
import { dbService, type TtsVoice, type User } from '../services/database'

const router = useRouter()
const currentUser = ref<User | null>(null)

const loading = ref(false)
const saving = ref(false)
const voices = ref<TtsVoice[]>([])
const thingsToTalkAboutToday = ref('')

const goDashboard = () => {
	router.push('/dashboard')
}

function addVoice() {
	voices.value.push({ voice_id: '', name: '', description: '' })
}

async function load() {
	loading.value = true
	try {
		const cfg = await dbService.getTtsConfig()
		voices.value = Array.isArray(cfg.voices) ? cfg.voices : []
		thingsToTalkAboutToday.value = cfg.thingsToTalkAboutToday || ''
	} catch (e) {
		console.error(e)
		alert('Failed to load voices')
	} finally {
		loading.value = false
	}
}

async function save() {
	saving.value = true
	try {
		const cleaned = (voices.value || [])
			.map((v) => ({
				voice_id: String(v.voice_id || '').trim(),
				name: String(v.name || '').trim(),
				description: String(v.description || '').trim()
			}))
			.filter((v) => Boolean(v.voice_id))

		const result = await dbService.updateTtsConfig({
			voices: cleaned,
			thingsToTalkAboutToday: thingsToTalkAboutToday.value
		})

		voices.value = Array.isArray(result.voices) ? result.voices : cleaned
		thingsToTalkAboutToday.value = result.thingsToTalkAboutToday || thingsToTalkAboutToday.value
		alert('Saved')
	} catch (e) {
		console.error(e)
		alert('Failed to save voices')
	} finally {
		saving.value = false
	}
}

onMounted(() => {
	try {
		const userStr = sessionStorage.getItem('currentUser')
		if (userStr) currentUser.value = JSON.parse(userStr)
	} catch {
		// ignore
	}
	void load()
})
</script>

<style scoped>
.voices-container {
	min-height: 100vh;
	background-color: #f5f5f5;
}
.page-main {
	padding: 2rem;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}
.panel {
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
	padding: 1.5rem;
}
.panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
}
.btn-row {
	display: flex;
	gap: 0.75rem;
}
.hint {
	color: #666;
	margin-top: 0.25rem;
	margin-bottom: 0.75rem;
}
.talk-box {
	width: 100%;
	border: 1px solid #ddd;
	border-radius: 8px;
	padding: 0.75rem;
	font-size: 1rem;
	box-sizing: border-box;
}
.voice-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
	padding: 1rem 0;
	border-top: 1px solid #eee;
}
.field label {
	display: block;
	font-size: 0.9rem;
	color: #555;
	margin-bottom: 0.35rem;
}
.input {
	width: 100%;
	border: 1px solid #ddd;
	border-radius: 8px;
	padding: 0.6rem 0.75rem;
	font-size: 1rem;
	box-sizing: border-box;
}
.action-btn {
	padding: 0.6rem 1.2rem;
	border: none;
	border-radius: 8px;
	background-color: #666;
	color: white;
	cursor: pointer;
	font-weight: 600;
}
.construct-btn {
	background-color: #4CAF50;
}
.action-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}
.loading {
	color: #666;
}
.unauthorized {
	background: white;
	border-radius: 8px;
	padding: 2rem;
	text-align: center;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
</style>
